/**
 * Утилита для проверки просрочки с учетом времени дня
 * Задача считается просроченной только после указанного времени (по умолчанию 18:00)
 */

/**
 * Проверяет, просрочена ли задача с учетом времени дня
 * @param dueDate - Дата дедлайна (может быть только датой или датой со временем)
 * @param overdueTime - Время, после которого задача считается просроченной (по умолчанию 18:00)
 * @param status - Статус задачи (просроченные задачи с определенными статусами не считаются просроченными)
 * @returns true, если задача просрочена
 */
export function isOverdue(
  dueDate: string | Date | null | undefined,
  overdueTime: string = '18:00',
  status?: string
): boolean {
  if (!dueDate) return false;

  // Если задача уже выполнена или приостановлена, она не считается просроченной
  if (status && ['COMPLETED', 'PAUSED', 'ACCEPTED', 'PENDING_APPROVAL'].includes(status)) {
    return false;
  }

  const now = new Date();
  const due = new Date(dueDate);
  
  // Нормализуем даты до начала дня для сравнения
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDateOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  // Если в dueDate указано время (не 00:00:00), используем его как есть
  const hasTime = due.getHours() !== 0 || due.getMinutes() !== 0 || due.getSeconds() !== 0;

  if (hasTime) {
    // Если дедлайн сегодня, задача считается просроченной только после 18:00 следующего дня
    // Это дает сотруднику полный день на выполнение задачи, созданной день в день
    if (nowDate.getTime() === dueDateOnly.getTime()) {
      // Дедлайн сегодня - даем до 18:00 следующего дня
      const [hours, minutes] = overdueTime.split(':').map(Number);
      const nextDayThreshold = new Date(dueDateOnly);
      nextDayThreshold.setDate(nextDayThreshold.getDate() + 1);
      nextDayThreshold.setHours(hours || 18, minutes || 0, 0, 0);
      return now > nextDayThreshold;
    }
    // Если дедлайн в прошлом (не сегодня), сравниваем как есть
    // Если дедлайн в будущем, задача не просрочена
    return now > due;
  }

  // Если указана только дата (без времени), считаем просроченной после указанного времени дня
  const [hours, minutes] = overdueTime.split(':').map(Number);
  
  // Если дедлайн сегодня, даем до 18:00 следующего дня
  if (nowDate.getTime() === dueDateOnly.getTime()) {
    const nextDayThreshold = new Date(dueDateOnly);
    nextDayThreshold.setDate(nextDayThreshold.getDate() + 1);
    nextDayThreshold.setHours(hours || 18, minutes || 0, 0, 0);
    return now > nextDayThreshold;
  }
  
  // Если дедлайн в прошлом (не сегодня), считаем просроченной после указанного времени дня дедлайна
  // Если дедлайн в будущем, задача не просрочена
  const overdueThreshold = new Date(dueDateOnly);
  overdueThreshold.setHours(hours || 18, minutes || 0, 0, 0);
  
  // Если дедлайн в прошлом, добавляем еще один день (18:00 следующего дня после дедлайна)
  if (nowDate > dueDateOnly) {
    overdueThreshold.setDate(overdueThreshold.getDate() + 1);
  }

  return now > overdueThreshold;
}

/**
 * Проверяет просрочку для задач протоколов
 */
export function isTaskOverdue(task: { dueDate: string | null; status: string }): boolean {
  return isOverdue(task.dueDate, '18:00', task.status);
}

/**
 * Проверяет просрочку для чек-листов
 */
export function isChecklistInstanceOverdue(instance: { dueAt: string | Date; status: string }): boolean {
  return isOverdue(instance.dueAt, '18:00', instance.status);
}


